import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Settings2, Save, Loader2, X, Plus, GripVertical } from 'lucide-react';
import { settingsApi, domainsApi } from '../services/api';
import { GROUP_BY_OPTIONS } from '../constants/reportOptions';

export default function Settings() {
  const queryClient = useQueryClient();
  const [defaultReport, setDefaultReport] = useState('');
  const [defaultDate, setDefaultDate] = useState('today');
  const [timezoneFilter, setTimezoneFilter] = useState('');
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);
  const [offerFilter, setOfferFilter] = useState('');
  const [showOfferDropdown, setShowOfferDropdown] = useState(false);
  const [defaultGroupBy, setDefaultGroupBy] = useState([]);
  const [groupByFilter, setGroupByFilter] = useState('');
  const [showGroupByDropdown, setShowGroupByDropdown] = useState(false);

  // Fetch user settings
  const { data: settingsData } = useQuery({
    queryKey: ['user-settings'],
    queryFn: () => settingsApi.getSettings(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch domains for offer selection
  const { data: domainsData } = useQuery({
    queryKey: ['domains'],
    queryFn: () => domainsApi.getDomains(),
    staleTime: 5 * 60 * 1000,
  });

  const domains = domainsData?.data?.data || [];
  const defaultOffer = domainsData?.data?.defaultOffer;
  const settings = settingsData?.data?.data || {};

  // Initialize state from settings
  useEffect(() => {
    if (settings.default_report) {
      setDefaultReport(settings.default_report);
    }
    if (settings.default_date?.dateType) {
      setDefaultDate(settings.default_date.dateType);
    }
    if (settings.timezone) {
      setTimezoneFilter(settings.timezone);
    }
    if (settings.default_groupby && Array.isArray(settings.default_groupby)) {
      // Convert field names to full option objects
      const groups = settings.default_groupby
        .map(field => GROUP_BY_OPTIONS.find(opt => opt.field === field))
        .filter(Boolean);
      setDefaultGroupBy(groups);
    }
  }, [settings]);

  // Get all timezones
  const timezones = Intl.supportedValuesOf('timeZone');
  const filteredTimezones = timezoneFilter
    ? timezones.filter(tz => tz.toLowerCase().includes(timezoneFilter.toLowerCase()))
    : timezones;

  // Filter domains for autocomplete
  const filteredDomains = offerFilter
    ? domains.filter(d => d.name?.toLowerCase().includes(offerFilter.toLowerCase()))
    : domains;

  // Mutations
  const saveReportMutation = useMutation({
    mutationFn: (reportType) => settingsApi.saveDefaultReport(reportType),
    onSuccess: () => queryClient.invalidateQueries(['user-settings']),
  });

  const saveDateMutation = useMutation({
    mutationFn: (dateType) => settingsApi.saveDefaultDate({ dateType }),
    onSuccess: () => queryClient.invalidateQueries(['user-settings']),
  });

  const saveTimezoneMutation = useMutation({
    mutationFn: (timezone) => settingsApi.saveTimezone(timezone),
    onSuccess: () => queryClient.invalidateQueries(['user-settings']),
  });

  const saveOfferMutation = useMutation({
    mutationFn: ({ dkey, offerName }) => domainsApi.saveDefaultOffer(dkey, offerName),
    onSuccess: () => queryClient.invalidateQueries(['domains']),
  });

  const saveGroupByMutation = useMutation({
    mutationFn: (groupByFields) => settingsApi.saveDefaultGroupBy(groupByFields),
    onSuccess: () => queryClient.invalidateQueries(['user-settings']),
  });

  const handleSave = async () => {
    try {
      await Promise.all([
        saveReportMutation.mutateAsync(defaultReport),
        saveDateMutation.mutateAsync(defaultDate),
        saveGroupByMutation.mutateAsync(defaultGroupBy.map(g => g.field)),
      ]);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  // Filter group by options based on search and exclude already selected ones
  const availableGroupByOptions = GROUP_BY_OPTIONS.filter(opt =>
    !defaultGroupBy.some(g => g.field === opt.field) &&
    opt.label.toLowerCase().includes(groupByFilter.toLowerCase())
  );

  const handleAddGroupBy = (option) => {
    if (defaultGroupBy.length < 5) {
      setDefaultGroupBy([...defaultGroupBy, option]);
    }
    setGroupByFilter('');
    setShowGroupByDropdown(false);
  };

  const handleRemoveGroupBy = (field) => {
    setDefaultGroupBy(defaultGroupBy.filter(g => g.field !== field));
  };

  const handleClearOffer = async () => {
    try {
      await saveOfferMutation.mutateAsync({ dkey: null, offerName: 'Default Offer Deleted' });
      toast.success('Default offer cleared');
    } catch (error) {
      toast.error('Failed to clear default offer');
    }
  };

  const handleSelectOffer = async (domain) => {
    try {
      await saveOfferMutation.mutateAsync({ dkey: domain.dkey, offerName: domain.name });
      setOfferFilter('');
      setShowOfferDropdown(false);
      toast.success('Default offer updated');
    } catch (error) {
      toast.error('Failed to update default offer');
    }
  };

  const handleSelectTimezone = async (tz) => {
    try {
      await saveTimezoneMutation.mutateAsync(tz);
      setTimezoneFilter(tz);
      setShowTimezoneDropdown(false);
      toast.success('Timezone updated');
    } catch (error) {
      toast.error('Failed to update timezone');
    }
  };

  const isSaving = saveReportMutation.isPending || saveDateMutation.isPending || saveGroupByMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-3">
            <Settings2 className="w-5 h-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-secondary-900">User Settings</h2>
          </div>
          <p className="text-sm text-secondary-500 mt-1">
            Below are your personal settings, clicking 'Save' will refresh the page.
          </p>
        </div>

        <div className="card-body space-y-6">
          {/* Default Report & Default Date Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Default Report</label>
              <select
                value={defaultReport}
                onChange={(e) => setDefaultReport(e.target.value)}
                className="input"
              >
                <option value="">Select a report...</option>
                <option value="variant">Variant</option>
                <option value="channel">Channel</option>
                <option value="subchannel">SubChannel</option>
                <option value="keyword">Keyword</option>
                <option value="country">Country</option>
                <option value="device">Device</option>
              </select>
            </div>

            <div>
              <label className="label">Default Date</label>
              <select
                value={defaultDate}
                onChange={(e) => setDefaultDate(e.target.value)}
                className="input"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7days">Last 7 Days</option>
                <option value="last30days">Last 30 Days</option>
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
              </select>
            </div>
          </div>

          {/* Current Default Offer & Change Default Offer Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Current Default Offer</label>
              {defaultOffer ? (
                <div className="flex items-center gap-2">
                  <span className="flex-1 px-3 py-2 bg-secondary-100 rounded-lg text-sm text-secondary-700">
                    {defaultOffer.name}
                  </span>
                  <button
                    onClick={handleClearOffer}
                    className="p-2 text-secondary-400 hover:text-red-500 transition-colors"
                    title="Clear default offer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="px-3 py-2 bg-secondary-50 rounded-lg text-sm text-secondary-400 italic">
                  No default offer set
                </div>
              )}
            </div>

            <div>
              <label className="label">Change Default Offer</label>
              <div className="relative">
                <input
                  type="text"
                  value={offerFilter}
                  onChange={(e) => {
                    setOfferFilter(e.target.value);
                    setShowOfferDropdown(true);
                  }}
                  onFocus={() => setShowOfferDropdown(true)}
                  placeholder="Start typing..."
                  className="input"
                />
                {showOfferDropdown && filteredDomains.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-secondary-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                    {filteredDomains.slice(0, 20).map((domain) => (
                      <button
                        key={domain.dkey}
                        onClick={() => handleSelectOffer(domain)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-primary-50"
                      >
                        {domain.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Current Time zone & Change Time zone Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Current Time zone</label>
              <div className="px-3 py-2 bg-secondary-100 rounded-lg text-sm text-secondary-700">
                {settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
              </div>
            </div>

            <div>
              <label className="label">Change Time zone</label>
              <div className="relative">
                <input
                  type="text"
                  value={timezoneFilter}
                  onChange={(e) => {
                    setTimezoneFilter(e.target.value);
                    setShowTimezoneDropdown(true);
                  }}
                  onFocus={() => setShowTimezoneDropdown(true)}
                  placeholder="Start typing..."
                  className="input"
                />
                {showTimezoneDropdown && filteredTimezones.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-secondary-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                    {filteredTimezones.slice(0, 20).map((tz) => (
                      <button
                        key={tz}
                        onClick={() => handleSelectTimezone(tz)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-primary-50"
                      >
                        {tz}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Default Group By Fields for Analytics Reports */}
          <div>
            <label className="label">Default Group By Fields (Analytics Reports)</label>
            <p className="text-xs text-secondary-500 mb-2">
              Select up to 5 fields. If nothing selected, defaults to Channel and Subchannel.
            </p>

            {/* Selected fields */}
            <div className="flex flex-wrap gap-2 mb-3">
              {defaultGroupBy.map((group, index) => (
                <div
                  key={group.field}
                  className="flex items-center gap-1 px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-lg text-sm"
                >
                  <span className="text-primary-700">{index + 1}. {group.label}</span>
                  <button
                    onClick={() => handleRemoveGroupBy(group.field)}
                    className="ml-1 text-primary-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {defaultGroupBy.length === 0 && (
                <div className="px-3 py-1.5 bg-secondary-50 rounded-lg text-sm text-secondary-400 italic">
                  No fields selected (will use Channel, Subchannel)
                </div>
              )}
            </div>

            {/* Add new field */}
            {defaultGroupBy.length < 5 && (
              <div className="relative">
                <input
                  type="text"
                  value={groupByFilter}
                  onChange={(e) => {
                    setGroupByFilter(e.target.value);
                    setShowGroupByDropdown(true);
                  }}
                  onFocus={() => setShowGroupByDropdown(true)}
                  onBlur={() => setTimeout(() => setShowGroupByDropdown(false), 200)}
                  placeholder="Search and add group by field..."
                  className="input"
                />
                {showGroupByDropdown && availableGroupByOptions.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-secondary-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                    {availableGroupByOptions.slice(0, 15).map((option) => (
                      <button
                        key={option.field}
                        onClick={() => handleAddGroupBy(option)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-primary-50 flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4 text-secondary-400" />
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-secondary-200 flex gap-3">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn btn-primary"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
